import React, { useState, useEffect } from 'react';
import {
    Typography,
    Button,
    Card,
    CardContent,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    MenuItem,
    Box,
    Container,
    IconButton,
    Divider,
    Snackbar,
    Alert,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { AddCircle, Cancel, Close, Save } from '@mui/icons-material';


const theme = createTheme({
    palette: {
        primary: {
            main: '#1976d2',
        },
        secondary: {
            main: '#dc004e',
        },
        background: {
            default: '#f5f5f5',
        },
    },
    typography: {
        h4: {
            fontWeight: 600,
        },
        h6: {
            fontWeight: 500,
        },
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    textTransform: 'none',
                    fontWeight: 600,
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                head: {
                    fontWeight: 'bold',
                    backgroundColor: '#f5f5f5',
                },
            },
        },
    },
});

const DesignationManagement = () => {
    // State management
    const [screen, setScreen] = useState('list');
    const [selectedItem, setSelectedItem] = useState(null);
    const [designations, setDesignations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    const [deleteDialog, setDeleteDialog] = useState({
        open: false,
        itemId: null
    });

    // Form state for Create and Edit
    const [formValues, setFormValues] = useState({
        department: '',
        name: ''
    });

    // Fetch data on component mount
    useEffect(() => {
        fetchDesignations();
        fetchDepartments();
    }, []);

    // API calls
    const fetchDesignations = async () => {
        try {
            const response = await fetch(`http://biometick.com/get-designation/${localStorage.getItem("userID")}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            const data = await response.json();
            if (data.length > 0) {
                setDesignations(data);
            }
        } catch (error) {
            showSnackbar('Failed to fetch designations', 'error');
        }
    };

    const fetchDepartments = async () => {
        try {
            const response = await fetch(`http://biometick.com/get-departments/${localStorage.getItem("userID")}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            const data = await response.json();
            if (data.length > 0) {
                setDepartments(data);
            }
        } catch (error) {
            showSnackbar('Failed to fetch departments', 'error');
        }
    };

    const createDesignation = async () => {
        try {
            const { name, department } = formValues;

            const myHeaders = new Headers();
            myHeaders.append("Authorization", `Bearer ${localStorage.getItem("token")}`);
            myHeaders.append("Content-Type", "application/x-www-form-urlencoded");

            const urlencoded = new URLSearchParams();
            urlencoded.append("userID", localStorage.getItem("userID"));
            urlencoded.append("name", name);
            urlencoded.append("department", department);

            const response = await fetch("http://biometick.com/post-designation", {
                method: 'POST',
                headers: myHeaders,
                body: urlencoded,
            });

            const result = await response.json();

            if (result._id) {
                showSnackbar('Designation created successfully');
                setScreen('list');
                fetchDesignations();
                resetForm();
            } else {
                showSnackbar('Failed to create designation', 'error');
            }
        } catch (error) {
            showSnackbar('An error occurred', 'error');
        }
    };

    const updateDesignation = async () => {
        try {
            const response = await fetch(`http://biometick.com/modify-designation/${selectedItem._id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
                body: JSON.stringify(formValues),
            });

            const result = await response.json();

            if (result._id) {
                showSnackbar('Designation updated successfully');
                setScreen('list');
                fetchDesignations();
                resetForm();
            } else {
                showSnackbar('Failed to update designation', 'error');
            }
        } catch (error) {
            showSnackbar('An error occurred', 'error');
        }
    };

    const deleteDesignation = async (id) => {
        try {
            const response = await fetch(`http://biometick.com/delete-designation/${id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (response.ok) {
                setDesignations(designations.filter(designation => designation._id !== id));
                showSnackbar('Designation deleted successfully');
            } else {
                showSnackbar('Failed to delete designation', 'error');
            }
        } catch (error) {
            showSnackbar('An error occurred', 'error');
        }
    };

    // Helper functions
    const handleFormChange = (event) => {
        const { name, value } = event.target;
        setFormValues(prevValues => ({
            ...prevValues,
            [name]: value,
        }));
    };

    const handleEdit = (item) => {
        setSelectedItem(item);
        setFormValues({
            department: item.department || '',
            name: item.name || ''
        });
        setScreen('edit');
    };

    const handleDeleteClick = (id) => {
        setDeleteDialog({
            open: true,
            itemId: id
        });
    };

    const confirmDelete = () => {
        deleteDesignation(deleteDialog.itemId);
        setDeleteDialog({
            open: false,
            itemId: null
        });
    };

    const resetForm = () => {
        setFormValues({
            department: '',
            name: ''
        });
        setSelectedItem(null);
    };

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({
            open: true,
            message,
            severity
        });
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({
            ...prev,
            open: false
        }));
    };

    const getDepartmentName = (departmentId) => {
        const department = departments.find(dept => dept._id === departmentId);
        return department ? department.name : 'N/A';
    };

    // Render components based on current screen
    const renderDesignationsList = () => (
        <TableContainer component={Paper} elevation={2} sx={{ mt: 3, borderRadius: 2, overflow: 'hidden' }}>
            <Table aria-label="department table">
                <TableHead>
                    <TableRow>
                        <TableCell>Designation Name</TableCell>
                        <TableCell>Department</TableCell>
                        <TableCell align="right">Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {designations.length > 0 ? (
                        designations.map((designation) => (
                            <TableRow key={designation._id} sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}>
                                <TableCell>{designation.name}</TableCell>
                                <TableCell>{getDepartmentName(designation.department)}</TableCell>
                                <TableCell align="right">
                                    <IconButton
                                        color="primary"
                                        onClick={() => handleEdit(designation)}
                                        size="small"
                                        sx={{ mr: 1 }}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton
                                        color="error"
                                        onClick={() => handleDeleteClick(designation._id)}
                                        size="small"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <TableCell colSpan={3} align="center">No designations found</TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );

    const renderForm = () => (
        <Card sx={{ width: '100%', mt: 3 }} elevation={3}>
            <CardContent>
                <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            label="Designation Name"
                            name="name"
                            variant="outlined"
                            fullWidth
                            value={formValues.name}
                            onChange={handleFormChange}
                            required
                        />
                        <TextField
                            select
                            label="Department"
                            name="department"
                            variant="outlined"
                            fullWidth
                            value={formValues.department}
                            onChange={handleFormChange}
                            required
                        >
                            {departments.map((option) => (
                                <MenuItem key={option._id} value={option._id}>
                                    {option.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>

                    <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
                        <Button
                            variant="outlined"
                            startIcon={<Cancel />}
                            onClick={() => {
                                resetForm();
                                setScreen('list');
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={screen === 'create' ? <Save /> : <AddCircle />}
                            onClick={screen === 'create' ? createDesignation : updateDesignation}
                            color="primary"
                        >
                            {screen === 'create' ? 'Create Designation' : 'Update Designation'}
                        </Button>
                    </Stack>
                </Box>
            </CardContent>
        </Card>
    );

    return (<ThemeProvider theme={theme}>
        <Container maxWidth="lg" sx={{ py: 2 }}>
            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h4" color="primary">
                        {screen === 'list' && 'Designation'}
                        {screen === 'create' && 'Create Designation'}
                        {screen === 'edit' && 'Edit Designation'}
                    </Typography>

                    {screen === 'list' ? <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => {
                            resetForm();
                            setScreen('create');
                        }}
                    >
                        Add Designation
                    </Button> : <Button
                        variant="contained"
                        color="warning"
                        startIcon={<Close />}
                        onClick={() => {
                            resetForm();
                            setScreen('list');
                        }}
                    >
                        Cancel
                    </Button>}
                </Box>
                <Divider sx={{ mt: 2 }} />
            </Box>
            {screen === 'list' && renderDesignationsList()}
            {(screen === 'create' || screen === 'edit') && renderForm()}

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Confirmation Dialog for Delete */}
            <Dialog
                open={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, itemId: null })}
            >
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this designation? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialog({ open: false, itemId: null })} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={confirmDelete} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    </ThemeProvider>);
};

export default DesignationManagement;